import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { createBuilder, getBuilder, listDrafts, publishDraft, setLiveVersion } from './client';
import { keys } from './keys';

/**
 * Agent Builder (ADR-0080, AB-2-2). Drafts change only when Forge saves one, at
 * the end of a builder turn — the conversation invalidates both keys then — so
 * neither query polls.
 */
export function useDrafts() {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.drafts(),
    queryFn: ({ signal }) => listDrafts({ getAccessToken, signal }),
    select: (response) => response.data.drafts,
  });
}

/** One builder's agent (null until Forge's first save) + its revisions, oldest first. */
export function useBuilder(workstreamId: string) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.builder(workstreamId),
    queryFn: ({ signal }) => getBuilder(workstreamId, { getAccessToken, signal }),
    select: (response) => response.data,
  });
}

/**
 * "New agent" — opens a builder workstream. Settles only once the workstream
 * list has refetched, so the caller navigates to a workstream the page already
 * knows is a builder (no flash of the ordinary view).
 */
export function useCreateBuilder() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.createBuilder(),
    mutationFn: () => createBuilder({ getAccessToken }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.workstream.listMine() }),
  });
}

/**
 * Publish the builder's latest revision (`version` omitted) or roll back to an earlier
 * version (AB-2-4). Either moves the live version, so the builder, the drafts list
 * (its status) and the invite picker (a private agent appears) all refetch.
 */
export function useSetLiveVersion(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.liveVersion(workstreamId),
    mutationFn: (version?: number) =>
      version === undefined
        ? publishDraft(workstreamId, { getAccessToken })
        : setLiveVersion(workstreamId, version, { getAccessToken }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.builder(workstreamId) }),
        queryClient.invalidateQueries({ queryKey: keys.drafts() }),
        queryClient.invalidateQueries({ queryKey: keys.orgAgents() }),
      ]),
  });
}
